import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import { extractApiErrorMessage } from "../../../utils/util";
import { ContentProfileSummary } from "../../../api/admin";
import { isReady } from "../utils/profileStatus";
import {
  useCreateContentProfile,
  useUpdateContentProfile,
} from "../hooks/useContentProfiles";

// <input type="datetime-local"> works in local time without a zone.
const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

interface ContentProfileModalProps {
  open: boolean;
  onClose: () => void;
  // Null creates a new profile (and navigates into it once created); a
  // summary edits its name, description, order and deadline in place. Settings/collections/devices are only ever
  // edited on the profile's own detail page, never here.
  profile?: ContentProfileSummary | null;
}

const ContentProfileModal = ({
  open,
  onClose,
  profile,
}: ContentProfileModalProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState<"devices" | "order">("devices");
  const [orderName, setOrderName] = useState("");
  const [deadline, setDeadline] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate: create, isPending: isCreating } = useCreateContentProfile();
  const { mutate: update, isPending: isUpdating } = useUpdateContentProfile();

  useEffect(() => {
    if (open) {
      setName(profile?.name ?? "");
      setDescription(profile?.description ?? "");
      setTarget(profile?.orderName ? "order" : "devices");
      setOrderName(profile?.orderName ?? "");
      setDeadline(toLocalInput(profile?.deadline));
      setError(null);
    }
  }, [open, profile]);

  const trimmedName = name.trim();
  const trimmedOrder = target === "order" ? orderName.trim() : "";
  // A ready profile only takes a new deadline.
  const ready = !!profile && isReady(profile);
  // The API only lets the target change while the profile has no devices.
  const targetLocked = ready || !!profile?.deviceCount;
  // The deadline field hides without an order, and is cleared with it.
  const effectiveDeadline = trimmedOrder ? deadline : "";
  const isPending = isCreating || isUpdating;
  const canSave =
    !isPending &&
    trimmedName.length > 0 &&
    (target === "devices" || trimmedOrder.length > 0);

  const handleError = (e: unknown) => {
    setError(extractApiErrorMessage(e, "Something went wrong"));
  };

  const handleSave = () => {
    setError(null);
    const trimmedDescription = description.trim() || null;
    const targetFields = {
      orderName: trimmedOrder || null,
      deadline: effectiveDeadline
        ? new Date(effectiveDeadline).toISOString()
        : null,
    };
    if (profile) {
      update(
        {
          id: profile.id,
          data: {
            // The API refuses any other field on a ready profile, even
            // unchanged.
            ...(ready
              ? {}
              : { name: trimmedName, description: trimmedDescription }),
            // Unchanged fields aren't sent, so an untouched order on a
            // profile with devices never trips the API's target lock.
            ...(targetFields.orderName !== (profile.orderName ?? null)
              ? { orderName: targetFields.orderName }
              : {}),
            ...(effectiveDeadline !== toLocalInput(profile.deadline)
              ? { deadline: targetFields.deadline }
              : {}),
          },
        },
        { onSuccess: onClose, onError: handleError },
      );
    } else {
      create(
        { name: trimmedName, description: trimmedDescription, ...targetFields },
        {
          onSuccess: (created) => {
            onClose();
            navigate(`/admin/profiles/${created.id}`);
          },
          onError: handleError,
        },
      );
    }
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>
        {profile ? `Edit ${profile.name}` : "New content profile"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DSTextField
            label="Name"
            size="small"
            disabled={ready}
            value={name}
            placeholder="e.g. Lobby clocks"
            onChange={(e) => setName(e.target.value)}
          />
          <DSTextField
            label="Description"
            size="small"
            multiline
            rows={2}
            disabled={ready}
            value={description}
            placeholder="Optional — what is this profile for?"
            onChange={(e) => setDescription(e.target.value)}
          />
          <DSBox>
            <DSTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Target
            </DSTypography>
            <DSSegmentedTabs
              size="small"
              options={[
                { label: "Devices", value: "devices", disabled: targetLocked },
                { label: "Order", value: "order", disabled: targetLocked },
              ]}
              value={target}
              onChange={(value) => value && setTarget(value)}
            />
            <DSTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {ready
                ? "The profile is ready: only the deadline can change."
                : targetLocked
                  ? "The target can't change once the profile has devices."
                  : target === "devices"
                    ? "Pick devices on the profile's page, then push with Sync."
                    : "Pushed to the order's clocks automatically once the order has all of them."}
            </DSTypography>
          </DSBox>
          {target === "order" && (
            <DSTextField
              label="Order"
              size="small"
              value={orderName}
              disabled={targetLocked}
              placeholder="e.g. P21234"
              onChange={(e) => setOrderName(e.target.value)}
            />
          )}
          {target === "order" && (
            <DSTextField
              label="Deadline"
              size="small"
              type="datetime-local"
              value={deadline}
              InputLabelProps={{ shrink: true }}
              helperText="Optional — an alert fires if the profile isn't submitted by then."
              onChange={(e) => setDeadline(e.target.value)}
            />
          )}
          {!profile && (
            <DSTypography variant="caption" color="text.secondary">
              Settings, collections and devices are configured on the profile's
              own page after it's created.
            </DSTypography>
          )}
          {error && (
            <DSTypography variant="caption" color="error">
              {error}
            </DSTypography>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <DSButton size="small" onClick={onClose}>
          Cancel
        </DSButton>
        <DSButton
          variant="contained"
          size="small"
          disabled={!canSave}
          onClick={handleSave}
        >
          {profile ? "Save" : "Create"}
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default ContentProfileModal;
